"use client";

import { MapPin, Rocket } from "lucide-react";
import { Container } from "@/components/primitives/Container";
import { Eyebrow } from "@/components/primitives/Eyebrow";
import { InvestorDeckCTA } from "@/components/sections/InvestorDeckCTA";
import { getAbout } from "@/content/about";
import { getAudiences } from "@/content/audiences";
import { useLocale } from "@/lib/i18n/provider";

/**
 * Investor-facing section: stage + HQ facts only — no TAM/SAM or traction charts.
 * Adds a forward-looking closed-launch focus list and a LinkedIn CTA from About leadership.
 */
export function ForInvestors() {
  const { locale } = useLocale();
  const copy = getAudiences(locale).investors;
  const about = getAbout(locale);
  const founder = about.leadership.entries.find(
    (entry) => entry.type === "person" && Boolean(entry.linkedin),
  );
  const focusItems = copy.focus ?? [];
  const focusTitle = copy.focusTitle;
  const linkedinLabel = copy.linkedinCtaLabel ?? "Connect on LinkedIn";

  return (
    <section id="investors" className="relative py-24 md:py-32">
      <Container>
        <div className="grid items-center gap-12 lg:grid-cols-[1.05fr_1fr] lg:gap-20">
          <div className="max-w-[62ch]">
            <Eyebrow>{copy.eyebrow}</Eyebrow>
            <h2 className="mt-4 text-[clamp(2rem,4vw,3rem)] font-bold leading-[1.06] tracking-[-0.025em] text-[var(--color-text-primary)]">
              {copy.title}
            </h2>
            <p className="mt-5 text-[clamp(1rem,1.2vw,1.125rem)] leading-relaxed text-[var(--color-text-secondary)]">
              {copy.body}
            </p>

            {focusItems.length > 0 ? (
              <div className="mt-8">
                {focusTitle ? (
                  <p className="text-[12px] font-semibold uppercase tracking-[0.14em] text-[var(--color-accent-base)]">
                    {focusTitle}
                  </p>
                ) : null}
                <ul className="mt-3 flex flex-col gap-2.5">
                  {focusItems.map((item) => (
                    <li
                      key={item}
                      className="flex items-start gap-2.5 text-[15px] leading-relaxed text-[var(--color-text-secondary)]"
                    >
                      <span
                        className="mt-2 inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-[var(--color-accent-base)]"
                        aria-hidden="true"
                      />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}

            <div className="mt-8 flex flex-wrap items-center gap-4">
              <InvestorDeckCTA label={copy.cta.label} />
              {founder?.linkedin ? (
                <a
                  href={founder.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex h-12 items-center justify-center rounded-2xl border border-[var(--color-surface-border)] px-5 text-sm font-semibold text-[var(--color-text-secondary)] transition-colors hover:border-[var(--color-surface-border-strong)] hover:text-[var(--color-text-primary)]"
                >
                  {linkedinLabel}
                </a>
              ) : null}
            </div>
          </div>

          <div className="relative">
            <div
              aria-hidden
              className="pointer-events-none absolute -inset-10 -z-10 rounded-full blur-[80px]"
              style={{
                background:
                  "radial-gradient(50% 50% at 50% 50%, rgba(var(--glow-accent-rgb), 0.22) 0%, rgba(var(--glow-accent-rgb), 0) 70%)",
              }}
            />
            <div className="rounded-3xl border border-[var(--color-surface-border)] bg-[var(--color-surface-glass)] p-6 md:p-8">
              <ul className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                {copy.facts.map((fact) => {
                  const Icon = fact.icon === "stage" ? Rocket : MapPin;
                  return (
                    <li
                      key={fact.label}
                      className="rounded-2xl bg-[var(--color-bg-raised)] p-5"
                    >
                      <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-[var(--color-accent-base)]">
                        <Icon size={14} strokeWidth={2} aria-hidden />
                        {fact.label}
                      </div>
                      <div className="mt-3 text-2xl font-bold tracking-tight text-[var(--color-text-primary)]">
                        {fact.value}
                      </div>
                      {fact.hint ? (
                        <div className="mt-1 text-[13px] leading-snug text-[var(--color-text-secondary)]">
                          {fact.hint}
                        </div>
                      ) : null}
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        </div>
      </Container>
    </section>
  );
}

export default ForInvestors;
